/**
 * TanStack Query hooks for the HackerOne submit modal.
 *
 * Three hooks:
 *  - useHackerOnePrograms   – fetches the full program list from GET /hackerone/programs
 *  - useHackerOneScopes     – fetches eligible scopes for a given program handle
 *  - useHackerOneWeaknesses – fetches weaknesses / CWEs for a given program handle
 *
 * Response shapes (raw arrays, no JSON:API envelope — the backend handler calls
 * aws.Api.Success(200, programs) which serialises the slice directly):
 *
 *   Program  { id, type, attributes: { handle, name, currency, submission_state } }
 *   Scope    { id, type, attributes: { asset_type, asset_identifier, eligible_for_bounty,
 *                                      eligible_for_submission, instruction } }
 *   Weakness { id, type, attributes: { name, description, external_id } }
 */

import { useQuery } from '@tanstack/react-query';

import { useAxios } from '@/hooks/useAxios';

// ---------------------------------------------------------------------------
// Wire types (match Go structs in backend/pkg/lib/hackerone/client.go)
// ---------------------------------------------------------------------------

export interface H1ProgramAttributes {
  handle: string;
  name: string;
  currency: string;
  submission_state: string;
}

export interface H1Program {
  id: string;
  type: string;
  attributes: H1ProgramAttributes;
}

export interface H1ScopeAttributes {
  asset_type: string;
  asset_identifier: string;
  eligible_for_bounty: boolean;
  eligible_for_submission: boolean;
  instruction: string;
}

export interface H1Scope {
  id: string;
  type: string;
  attributes: H1ScopeAttributes;
}

export interface H1WeaknessAttributes {
  name: string;
  description: string;
  external_id: string;
}

export interface H1Weakness {
  id: string;
  type: string;
  attributes: H1WeaknessAttributes;
}

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const hackerOneKeys = {
  programs: () => ['hackerone', 'programs'] as const,
  scopes: (handle: string) =>
    ['hackerone', 'programs', handle, 'scopes'] as const,
  weaknesses: (handle: string) =>
    ['hackerone', 'programs', handle, 'weaknesses'] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetches the list of HackerOne programs accessible to the authenticated user.
 * Enabled unconditionally — call with `enabled: open` at the call-site to
 * restrict fetching to when the modal is open.
 */
export function useHackerOnePrograms(enabled = true) {
  const axios = useAxios();

  return useQuery<H1Program[]>({
    queryKey: hackerOneKeys.programs(),
    queryFn: async () => {
      const { data } = await axios.get<H1Program[]>('/hackerone/programs');
      return data;
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes — program list changes rarely
    gcTime: 1000 * 60 * 10,
  });
}

/**
 * Fetches structured scopes for a given program handle.
 * Disabled automatically when `handle` is empty.
 */
export function useHackerOneScopes(handle: string) {
  const axios = useAxios();

  return useQuery<H1Scope[]>({
    queryKey: hackerOneKeys.scopes(handle),
    queryFn: async () => {
      const { data } = await axios.get<H1Scope[]>(
        `/hackerone/programs/${encodeURIComponent(handle)}/scopes`
      );
      return data;
    },
    enabled: handle.length > 0,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
  });
}

/**
 * Fetches weaknesses (CWEs) for a given program handle.
 * Disabled automatically when `handle` is empty.
 */
export function useHackerOneWeaknesses(handle: string) {
  const axios = useAxios();

  return useQuery<H1Weakness[]>({
    queryKey: hackerOneKeys.weaknesses(handle),
    queryFn: async () => {
      const { data } = await axios.get<H1Weakness[]>(
        `/hackerone/programs/${encodeURIComponent(handle)}/weaknesses`
      );
      return data;
    },
    enabled: handle.length > 0,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
  });
}
